/**
 * BracketBadge -- renders a bracket chip/badge with a hover tooltip explaining it.
 *
 * The bracket policy lives only in the backend (single source of truth). This component
 * renders the shown bracket, its position within the bracket (`bracket_position`), and the
 * backend's explanation (`BracketTooltipContent`: declaration, rule floor, plays-like
 * annotations) — it never recomputes a WotC rule.
 *
 * Supports three visual variants:
 *   'chip'   — dark background chip (DeckCard tiles): "B3 · high"
 *   'circle' — outlined circle badge
 *   'pill'   — small colored pill (ProfileView table): "B3 · high"
 */

import { BracketTooltipContent, bracketPositionText } from '@/components/BracketTooltipContent';
import { Tooltip } from '@/components/Tooltip';
import { BRACKET_COLORS } from '@/lib/constants';
import type { PowerLevelResult } from '@/lib/api';

interface BracketBadgeProps {
  bracket: number;
  variant: 'chip' | 'circle' | 'pill';
  powerLevel?: PowerLevelResult | null;
}

export function BracketBadge({ bracket, variant, powerLevel }: BracketBadgeProps) {
  const color = BRACKET_COLORS[bracket] ?? 'var(--outline-variant)';
  const label = bracketPositionText(bracket, powerLevel?.bracket_position);

  const tooltipContent = powerLevel?.minimum_bracket != null ? (
    <BracketTooltipContent bracket={bracket} powerLevel={powerLevel} />
  ) : null;

  if (variant === 'chip') {
    return (
      <Tooltip content={tooltipContent} interactive popupWidth="max-w-[320px]">
        <span
          className="chip cursor-default"
          style={{ background: color }}
          tabIndex={tooltipContent ? 0 : undefined}
        >
          {label}
        </span>
      </Tooltip>
    );
  }

  if (variant === 'circle') {
    return (
      <Tooltip content={tooltipContent} interactive popupWidth="max-w-[320px]">
        <div
          className="inline-flex items-center justify-center w-12 h-12 rounded-full text-3xl font-bold cursor-default"
          style={{ border: `3px solid ${color}`, color }}
          tabIndex={tooltipContent ? 0 : undefined}
        >
          {bracket}
        </div>
      </Tooltip>
    );
  }

  // pill
  return (
    <Tooltip content={tooltipContent} interactive popupWidth="max-w-[320px]">
      <span
        className="inline-block px-2 py-0.5 rounded-full text-xs font-bold cursor-default"
        style={{ color, border: `1px solid ${color}55`, background: `${color}18` }}
        tabIndex={tooltipContent ? 0 : undefined}
      >
        {label}
      </span>
    </Tooltip>
  );
}
